import type {
  MemAgentSlackConnectionContextEventParams,
  MemAgentSlackConnectSource,
} from "@/domains/metrics/tracked-event";
import type { SubscriptionPlanKind } from "@/store/subscriptions/types";

type MemAgentSlackConnectionContextForSource<Source extends MemAgentSlackConnectSource> = Omit<
  MemAgentSlackConnectionContextEventParams,
  "source"
> & {
  source: Source;
};

interface BuildMemAgentSlackConnectionContextArgs<Source extends MemAgentSlackConnectSource> {
  source: Source;
  isSetup: boolean;
  originalPlanKind?: SubscriptionPlanKind;
  originalEffectivePlanKind?: SubscriptionPlanKind;
}

interface BuildMemAgentSlackConnectionContextWithOriginalPlanArgs<
  Source extends MemAgentSlackConnectSource,
> extends BuildMemAgentSlackConnectionContextArgs<Source> {
  originalPlanKind: SubscriptionPlanKind;
  originalEffectivePlanKind: SubscriptionPlanKind;
}

type RequiredOriginalPlanConnectionContext = Required<
  Pick<
    MemAgentSlackConnectionContextEventParams,
    "original_plan_kind" | "original_effective_plan_kind"
  >
>;

/**
 * Builds the shared analytics context for Mem Agent Slack setup funnel events.
 *
 * Callers pass camelCase store values while this helper owns the snake_case
 * event field names used by Segment and Slack OAuth return storage.
 */
export function buildMemAgentSlackConnectionContext<Source extends MemAgentSlackConnectSource>(
  args: BuildMemAgentSlackConnectionContextWithOriginalPlanArgs<Source>
): MemAgentSlackConnectionContextForSource<Source> & RequiredOriginalPlanConnectionContext;
export function buildMemAgentSlackConnectionContext<Source extends MemAgentSlackConnectSource>(
  args: BuildMemAgentSlackConnectionContextArgs<Source>
): MemAgentSlackConnectionContextForSource<Source>;
export function buildMemAgentSlackConnectionContext<Source extends MemAgentSlackConnectSource>({
  source,
  isSetup,
  originalPlanKind,
  originalEffectivePlanKind,
}: BuildMemAgentSlackConnectionContextArgs<Source>): MemAgentSlackConnectionContextForSource<Source> {
  const context: MemAgentSlackConnectionContextForSource<Source> = {
    source,
    is_setup: isSetup,
  };

  if (originalPlanKind) {
    context.original_plan_kind = originalPlanKind;
  }

  if (originalEffectivePlanKind) {
    context.original_effective_plan_kind = originalEffectivePlanKind;
  }

  return context;
}
